/**
 * MergeActionCard - Content for CRM object merge actions.
 *
 * Renders LinkedPair with symmetric direction using the rich ContentComponent
 * from ENTITY_CONTENT_REGISTRY for both source and target (via
 * ActionEntityPreview) — same pattern as Link/Create/Update/Delete cards.
 * Source (destructive) is wrapped in an ObjectCard with destructive variant
 * to reinforce the "will be deleted" cue; target remains default. Each
 * entity's display name opens the CRM slideout when it exists in CRM.
 *
 * Shell is provided by the parent. Read-only.
 */

import type {
  ObjectMergedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import { isObjectMergedPayload } from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { ActionEntityPreview } from './ActionEntityPreview';
import { LinkedPair } from './LinkedPair';
import {
  ActionError,
  buildEnrichedEntityData,
  getEntityTypeLabel,
} from './shared';
import { ACTION_CARD_BODY_CLASS } from './shared/actionCardLayout';

export function MergeActionCard({
  action,
  mode,
  enrichedData,
  orgId,
}: ActionRendererProps) {
  if (!isObjectMergedPayload(action.payload)) {
    return <ActionError message='Invalid object merge payload' mode={mode} />;
  }

  const typedAction = action as ProposedAction<ObjectMergedEntityPayload>;
  const { entity_id, entity_type_name, payload } = typedAction.payload;
  const { source_entity_id } = payload;

  const entityType = enrichedData?.entity_type_name || entity_type_name;

  // Backend merge contract (apps/crm_events/api/events.py +
  // apps/actions/services/enrichment.py):
  //   payload.entity_id                = the surviving / canonical entity (kept)
  //   payload.payload.source_entity_id = the duplicate entity (deleted on merge)
  //   enrichedData (top level)         = describes the SURVIVOR
  //                                      (object_label / entity_type_name)
  //   enrichedData.source_object       = describes the DELETED source
  // target_object is never populated for merges — the survivor is the top-level
  // enriched data, so the "Will be deleted" side is the source and the "Will
  // remain" side is the top-level entity.

  // Deleted source — the "Will be deleted" side.
  // Fallback labels use only the entity type — never a raw/truncated
  // entity id.
  const deletedLabel =
    enrichedData?.source_object?.label || getEntityTypeLabel(entityType);
  const deletedEntityType =
    enrichedData?.source_object?.entity_type_name || entityType;
  const deletedEntityId = source_entity_id;

  // Surviving target — the "Will remain" side.
  const survivorLabel =
    enrichedData?.object_label || getEntityTypeLabel(entityType);
  const survivorEntityType = enrichedData?.entity_type_name || entityType;
  const survivorEntityId = entity_id;

  const deletedEnrichedData = buildEnrichedEntityData(
    enrichedData?.source_object,
    deletedLabel,
    deletedEntityType
  );
  const survivorEnrichedData = buildEnrichedEntityData(
    {
      label: survivorLabel,
      entity_type_name: survivorEntityType,
    },
    survivorLabel,
    survivorEntityType
  );

  return (
    <div className={ACTION_CARD_BODY_CLASS}>
      <LinkedPair
        source={
          <ActionEntityPreview
            entityId={deletedEntityId}
            entityType={deletedEntityType}
            label={deletedLabel}
            enrichedData={deletedEnrichedData}
            orgId={orgId}
            variant='destructive'
            compact
          />
        }
        target={
          <ActionEntityPreview
            entityId={survivorEntityId}
            entityType={survivorEntityType}
            label={survivorLabel}
            enrichedData={survivorEnrichedData}
            orgId={orgId}
            compact
          />
        }
        direction='symmetric'
        sourceLabel='Will be deleted'
        targetLabel='Will remain'
      />
    </div>
  );
}
